import React, { useState, useEffect, useCallback, useRef } from 'react';
import './LeadMagnetManagementPage.css';
import LeadMagnetList from '../../components/admin/lead-magnet/LeadMagnetList';
import LeadMagnetFormModal from '../../components/admin/lead-magnet/LeadMagnetFormModal';
import AssetManagement from '../../components/admin/lead-magnet/AssetManagement';
import { Button, Alert } from '../../components/common';
import { assetApi } from '../../services/apis/configuredApis';
import { currentUserHasCerebralAdminRole } from '../../utils/roleCheck';

function LeadMagnetManagementPage() {
  const [assets, setAssets] = useState([]);
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLeadMagnet, setSelectedLeadMagnet] = useState(null);
  
  // Refresh trigger for LeadMagnetList and AssetManagement
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Active tab: 'lead-magnets' or 'assets'
  const [activeTab, setActiveTab] = useState('lead-magnets');
  
  // Ref to control asset upload modal
  const assetManagementRef = useRef(null);

  // Load lead magnets callback - receives data from LeadMagnetList component
  const handleLeadMagnetsLoaded = useCallback((leadMagnetsList) => {
    setLeadMagnets(leadMagnetsList);
  }, []);

  // Load assets for LeadMagnetFormModal
  const loadAssets = useCallback(async () => {
    try {
      const response = await assetApi.assetListGet({
        search: null,
        mimeType: null,
        uploadedBy: null,
        limit: 100,
        offset: 0,
        assetType: 'lead_magnet',
      });
      
      const responseData = response?.data || response;
      const assetsList = responseData?.assets || [];
      // Transform assets for LeadMagnetFormModal (needs id, fileName, fileSize)
      const transformedAssets = assetsList.map(asset => ({
        id: asset.id,
        fileName: asset.file_name,
        fileSize: asset.file_size_bytes,
        fileType: asset.mime_type,
        // Keep original for API calls
        ...asset,
      }));
      setAssets(transformedAssets);
    } catch (err) {
      console.error('Error loading assets:', err);
      setAssets([]);
    }
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets, refreshTrigger]);

  const refreshLeadMagnetList = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCreateLeadMagnet = () => {
    setSelectedLeadMagnet(null);
    setShowCreateModal(true);
  };

  const handleEditLeadMagnet = (magnet) => {
    setSelectedLeadMagnet(magnet);
    setShowEditModal(true);
  };

  const handleLeadMagnetSuccess = () => {
    setSuccess(selectedLeadMagnet ? 'Lead magnet updated successfully' : 'Lead magnet created successfully');
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedLeadMagnet(null);
    refreshLeadMagnetList();
    setTimeout(() => setSuccess(null), 3000);
  };



  return (
    <div className="lead-magnet-management">
      <div className="lead-magnet-management__header">
        <div className="lead-magnet-management__header-left">
          <h1 className="lead-magnet-management__title">Lead Magnet Management</h1>
          <p className="lead-magnet-management__subtitle">
            Create and manage lead magnets and assets
          </p>
        </div>
        <div className="lead-magnet-management__header-actions">
          {activeTab === 'lead-magnets' && currentUserHasCerebralAdminRole() && (
            <Button
              variant="primary"
              onClick={handleCreateLeadMagnet}
              className="lead-magnet-management__btn"
            >
              + Create Lead Magnet
            </Button>
          )}
          {activeTab === 'assets' && currentUserHasCerebralAdminRole() && (
            <Button
              variant="primary"
              onClick={() => {
                if (assetManagementRef.current) {
                  assetManagementRef.current.openUploadModal();
                }
              }}
              className="lead-magnet-management__btn"
            >
              + Upload Asset
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="lead-magnet-management__tabs">
        <button
          className={`lead-magnet-management__tab ${activeTab === 'lead-magnets' ? 'lead-magnet-management__tab--active' : ''}`}
          onClick={() => setActiveTab('lead-magnets')}
        >
          Lead Magnets
        </button>
        <button
          className={`lead-magnet-management__tab ${activeTab === 'assets' ? 'lead-magnet-management__tab--active' : ''}`}
          onClick={() => setActiveTab('assets')}
        >
          Assets
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert
          variant="error"
          onClose={() => setError(null)}
          className="lead-magnet-management__alert"
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          onClose={() => setSuccess(null)}
          className="lead-magnet-management__alert"
        >
          {success}
        </Alert>
      )}

      {/* Content */}
      {activeTab === 'lead-magnets' ? (
        <LeadMagnetList
          onEdit={handleEditLeadMagnet}
          refreshTrigger={refreshTrigger}
          onLeadMagnetsLoaded={handleLeadMagnetsLoaded}
        />
      ) : (
        <AssetManagement
          ref={assetManagementRef}
          leadMagnets={leadMagnets}
          refreshTrigger={refreshTrigger}
          showUploadButton={false}
          onAssetChange={loadAssets}
        />
      )}

      {/* Modals */}
      {showCreateModal && (
        <LeadMagnetFormModal
          show={true}
          leadMagnet={null}
          assets={assets}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedLeadMagnet(null);
          }}
          onSuccess={handleLeadMagnetSuccess}
        />
      )}

      {showEditModal && selectedLeadMagnet && (
        <LeadMagnetFormModal
          show={true}
          leadMagnet={selectedLeadMagnet}
          assets={assets}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLeadMagnet(null);
          }}
          onSuccess={handleLeadMagnetSuccess}
        />
      )}
    </div>
  );
}

export default LeadMagnetManagementPage;

